import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { L } from "./TVScenes";

const DAYS = [["mo", "Mo"], ["di", "Di"], ["mi", "Mi"], ["do", "Do"], ["fr", "Fr"], ["sa", "Sa"], ["so", "So"]];
const field = "w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none";
const err = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Bitte Eingaben prüfen");

export default function TVAutomations() {
  const [rules, setRules] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const [a, s] = await Promise.all([api.get("/admin/tv/automations"), api.get("/admin/tv/scenes")]);
    setRules(a.data); setScenes(s.data);
  };
  useEffect(() => { load().catch(() => toast.error("Automationen konnten nicht geladen werden")); }, []);

  const payload = (r) => ({ name: r.name || "", time: r.time, days: r.days, scene_id: r.scene_id, active: r.active });
  const save = async () => {
    try {
      if (edit.id) { await api.put(`/admin/tv/automations/${edit.id}`, payload(edit)); } else { await api.post("/admin/tv/automations", payload(edit)); }
      toast.success("Automation gespeichert"); setEdit(null); load();
    } catch (e) { toast.error(err(e)); }
  };
  const toggle = async (r, active) => {
    try { await api.put(`/admin/tv/automations/${r.id}`, payload({ ...r, active })); load(); } catch (e) { toast.error(err(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Automation löschen?")) { return; }
    try { await api.delete(`/admin/tv/automations/${r.id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(err(e)); }
  };
  const sceneName = (id) => scenes.find((s) => s.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="tv-automations">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-xs text-[#A8A398] flex-1">Läuft serverseitig (Zeitzone Europe/Luxembourg) – auch wenn diese Seite geschlossen ist.</div>
        <button onClick={() => setEdit({ name: "", time: "20:00", days: ["fr", "sa"], scene_id: scenes[0]?.id || "", active: true })} data-testid="tv-auto-new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded text-[10px] uppercase tracking-widest hover:bg-[#EEDC82]">
          <Plus size={13} /> Neue Regel
        </button>
      </div>
      {edit && <AutoForm edit={edit} setEdit={setEdit} scenes={scenes} onSave={save} />}
      {!rules.length && <div className="card-lux p-8 text-center text-[#A8A398]">Noch keine Automationen.</div>}
      <div className="space-y-3">
        {rules.map((r) => (
          <div key={r.id} className="card-lux p-4 flex items-center gap-4 flex-wrap" data-testid={`tv-auto-${r.id}`}>
            <Clock size={18} className="text-[#D4AF37]" />
            <div className="font-heading text-2xl w-20">{r.time}</div>
            <div className="flex-1 min-w-[160px]">
              <div className="text-sm">{r.name || sceneName(r.scene_id)} <span className="text-[#A8A398]">→ {sceneName(r.scene_id)}</span></div>
              <div className="text-[11px] text-[#A8A398] mt-0.5">{DAYS.filter(([k]) => r.days.includes(k)).map(([, l]) => l).join(", ")}{r.last_run_date ? ` · zuletzt ${r.last_run_date}` : ""}</div>
            </div>
            <Switch checked={r.active} onCheckedChange={(v) => toggle(r, v)} data-testid={`tv-auto-active-${r.id}`} />
            <button onClick={() => setEdit({ ...r })} className="px-3 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37]" data-testid={`tv-auto-edit-${r.id}`}><Pencil size={13} /></button>
            <button onClick={() => remove(r)} className="px-3 py-2 border border-[#2A2A2A] rounded text-[#EE9CA1] hover:border-[#7A1921]" data-testid={`tv-auto-delete-${r.id}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AutoForm({ edit, setEdit, scenes, onSave }) {
  const toggleDay = (d) => setEdit({ ...edit, days: edit.days.includes(d) ? edit.days.filter((x) => x !== d) : [...edit.days, d] });
  return (
    <div className="card-lux p-5 grid sm:grid-cols-2 gap-4" data-testid="tv-auto-form">
      <L label="Name (optional)"><input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={field} data-testid="tv-auto-name" /></L>
      <L label="Uhrzeit"><input type="time" value={edit.time} onChange={(e) => setEdit({ ...edit, time: e.target.value })} className={field} data-testid="tv-auto-time" /></L>
      <L label="Szene">
        <select value={edit.scene_id} onChange={(e) => setEdit({ ...edit, scene_id: e.target.value })} className={field} data-testid="tv-auto-scene">
          {scenes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </L>
      <L label="Wochentage">
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => toggleDay(k)} data-testid={`tv-auto-day-${k}`}
              className={`w-10 h-9 rounded-full border text-xs ${edit.days.includes(k) ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#EEDC82]" : "border-[#2A2A2A] text-[#A8A398]"}`}>{l}</button>
          ))}
        </div>
      </L>
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <button onClick={() => setEdit(null)} className="px-4 py-2 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest" data-testid="tv-auto-cancel">Abbrechen</button>
        <button onClick={onSave} disabled={!edit.days.length || !edit.scene_id} className="px-4 py-2 bg-[#D4AF37] text-black rounded text-[10px] uppercase tracking-widest disabled:opacity-40" data-testid="tv-auto-save">Speichern</button>
      </div>
    </div>
  );
}
